import styled, { css } from 'styled-components';

// CONSTANTS
import { theme } from '../../constants/theme';
import { responsiveEndPoints } from '../../constants/responsive-endpoints';

interface SetpStyleProps {
  child?: boolean;
  closed?: boolean;
  parent?: boolean;
  completeParent?: boolean;
  stepNum?: number;
  active?: boolean;
  complete?: boolean;
}

export const StepContainer = styled.div<{ isOpened?: boolean }>`
  background: ${theme.colors.primary.transparentBg};
  width: 354px;
  max-width: 354px;
  min-width: 354px;
  box-sizing: border-box;

  @media (max-width: 1400px) {
    min-width: 286px;
    width: 286px;
  }

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    width: 100%;
    background: none;
    max-width: auto;
    min-width: auto;
    height: ${({ isOpened }) => (!isOpened ? '80px' : 'auto')};
    overflow: hidden;
    transition: height 0.3s ease-out;
  }
`;

export const StepContainerOpened = styled.div<{ isOpened?: boolean }>`
  display: flex;
  position: relative;

  * {
    display: flex;
    font-family: 'Clan Pro', sans-serif;
    font-weight: ${theme.weights.semibold};
  }

  background: ${theme.colors.primary.transparentBg};
  padding: 22px 24px 40px 24px;
  width: 100%;
  box-sizing: border-box;
  align-items: center;

  ${({ isOpened }) =>
    !isOpened &&
    css`
      padding: 22px 22px;
    `};
`;

export const ButtonArrowWrapper = styled.span<{ isOpened?: boolean }>`
  position: absolute;
  display: flex;
  right: ${({ isOpened }) => (isOpened ? '-4px' : '20px')};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ isOpened }) => isOpened && theme.colors.secondary.gray500};
  justify-content: center;
  align-items: center;
`;

export const LineContainer = styled.div`
  flex-direction: column;
  position: relative;
  min-width: 175px;
  height: min-content;
`;

export const StepHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 85vw;
  margin-right: -3px;

  svg {
    cursor: pointer;
  }
`;

export const Title = styled.h3`
  font-size: 11px;
  line-height: 16px;
  text-transform: uppercase;
  color: ${theme.colors.secondary.gray300};
  font-family: 'Clan Pro', sans-serif;
  font-weight: ${theme.weights.semibold};
  margin-bottom: 8px;
`;

export const LineHeight = styled.div`
  position: absolute;
  z-index: 5;
  width: 1px;
  height: -webkit-fill-available;
  margin: 27px 16px 30px;
  background: ${theme.colors.primary.transparentBorder};

  @media (max-width: ${responsiveEndPoints.mobile}px) {
    top: 20px;
    height: calc(100% - 55px);
  }
`;

export const SingleStepLine = styled.div<SetpStyleProps>`
  display: flex;
  font-family: 'Clan Pro', sans-serif;
  font-weight: ${theme.weights.semibold};
  position: relative;
  z-index: 10;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  height: 32px;

  ${({ child }) =>
    !!child &&
    css`
      height: 24px;
      margin-top: 18px;
    `}

  ${({ parent }) =>
    !!parent &&
    css`
      height: 32px;
      margin-top: 18px;
    `}
  
  ${({ completeParent }) =>
    !!completeParent &&
    css`
      height: 32px;
      margin-top: 18px;
    `}
  
  ${({ completeParent, stepNum }) =>
    !!completeParent &&
    stepNum == 2 &&
    css`
      height: 32px;
      margin-top: 18px;
    `}
`;

// Parent stepSingle customization
export const SingleStepNumberSquare = styled.div<SetpStyleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 24px;
  width: ${({ closed }) => (closed ? '32px' : '24px')};
  height: ${({ closed }) => (closed ? '32px' : '24px')};
  margin: ${({ closed, active }) => (closed ? '0px' : active ? '0px' : '0 4px')};
  min-width: 24px;
  line-height: 24px;
  background: ${theme.colors.primary.transparentBorder};
  border-radius: 8px;
  color: ${theme.colors.secondary.gray300};

  ${({ active }) =>
    !!active &&
    css`
      width: 32px;
      height: 32px;
      margin: 0px;
      line-height: 32px;
      background: ${theme.colors.primary.black};
      color: ${theme.colors.primary.white};
    `}

  ${({ complete }) =>
    !!complete &&
    css`
      background: ${theme.colors.primary.black};
      color: ${theme.colors.primary.white};
    `}
    
  @media (max-width: ${responsiveEndPoints.mobile}px) {
    & + div {
      margin-left: 20px;
      margin-right: 2px;
    }
  }
`;

export const SingleStepTitle = styled.h2<SetpStyleProps>`
  font-size: 14px;
  line-height: 24px;
  margin-left: 22px;
  color: ${theme.colors.secondary.gray200};
  font-family: 'Clan Pro', sans-serif;
  font-weight: ${theme.weights.bold};
  letter-spacing: 0.01em;
  ${({ active }) =>
    !!active &&
    css`
      color: ${theme.colors.primary.black};
    `}
`;

// Children stepSingle customization
export const SingleStepChildrenBox = styled.div`
  display: flex;
  width: 32px;
  min-width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
`;

export const SingleStepChildrenDot = styled.div<SetpStyleProps>`
  width: 11px;
  height: 11px;
  background: ${theme.colors.primary.transparentBg};
  border-radius: 50%;
  border: solid 3px ${theme.colors.primary.transparentBorder};
  box-sizing: border-box;

  ${({ complete }) =>
    !!complete &&
    css`
      width: 11px;
      height: 11px;
      background: ${theme.colors.primary.black};
      border-width: 3px;
    `}

  ${({ active }) =>
    !!active &&
    css`
      width: 11px;
      height: 11px;
      background: ${theme.colors.primary.black};
      border-width: 2px;
    `}
`;

export const SingleStepChildrenTitle = styled.h3<SetpStyleProps>`
  display: flex;
  font-size: 13px;
  line-height: 24px;
  margin-left: 22px;
  font-family: 'Clan Pro', sans-serif;
  font-weight: ${theme.weights.regular};
  color: ${theme.colors.secondary.gray200};
  align-items: center;
  justify-content: center;

  > span:last-child {
    margin-left: 8px;
  }

  ${({ active }) =>
    !!active &&
    css`
      color: ${theme.colors.primary.black};
    `}
`;

export const StepChildrenEllipsis = styled.span`
  display: flex;
  color: ${theme.colors.primary.bariBlue};
  position: relative;
  font-size: 0.9rem;

  &:after {
    content: '•••';
    font-size: 6.5px;
    letter-spacing: 1.5px;
  }
`;

export const StepChildrenLine = styled.span<{ complete: boolean }>`
  display: flex;
  background-color: ${({ complete }) =>
    complete ? theme.colors.primary.black : theme.colors.primary.transparentBorder};
  position: relative;
  width: 19px;
  height: 2px;
  border-radius: 2px;
`;
